import React from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import Utility from './../../utils/Utility';
import { slugify } from '../../utils/slugify';
import GenerateOfferButton from '../../components/GenerateOfferButton/GenerateOfferButton';
import CDERequestButton from '../../components/CDERequestButton/CDERequestButton';
import ProjectBadge from '../../components/ProjectBadge/ProjectBadge';
import SaveProcedureButton from '../../components/SaveProcedureButton/SaveProcedureButton';
import { useAuth } from '../../context/AuthContext';

import {
  HiOutlineOfficeBuilding,
  HiOutlineCurrencyDollar,
  HiOutlineCalendar,
  HiOutlineArrowRight,
  HiOutlineLockClosed,
} from 'react-icons/hi';

const AuctionItem = ({ auction, auctionType }) => {
  const navigate = useNavigate();
  const { hasActiveSubscription } = useAuth();
  
  // Helper function to safely parse and format dates
  const getFormattedDate = () => {
    try {
      let dateString = null;
      
      // Determine which date field to use based on auction type
      if (auctionType === 'CANotices') {
        dateString = auction.noticeStateDate || auction.publishedDate;
      } else {
        dateString = auction.publishedDate || auction.noticeStateDate;
      }
      
      if (!dateString) {
        return 'Data necunoscută';
      }
      
      const dateObj = new Date(dateString);
      
      // Validate date
      if (isNaN(dateObj.getTime())) {
        return 'Data necunoscută';
      }
      
      return format(dateObj, 'dd MMM yyyy');
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Data necunoscută';
    }
  };
  
  const formattedDate = getFormattedDate();
  
  // Get appropriate ID and title based on auction type
  const auctionId = auction.auctionId || auction.caNoticeId || auction.noticeId || 'N/A';
  const auctionTitle = auction.auctionTitle || auction.contractTitle || 'Titlu necunoscut';
  const authorityName = auction.authorityName || auction.contractingAuthorityNameAndFN || 'Autoritate necunoscută';
  const estimatedValue = auction.estimatedValue || auction.ronContractValue || 0;
  const currency = auction.currency || auction.currencyCode || 'RON';

  const handleNavigation = () => {
    const slug = slugify(auctionTitle);
    const navId = auction.mongoId || auctionId;
    if (auctionType === 'DirectAwards') {
      navigate(`/achizitii-directe/${slug}/${navId}`);
    } else if (auctionType === 'ProcurementProcedures') {
      navigate(`/licitatii/${slug}/${navId}`);
    } else if (auctionType === 'CANotices') {
      navigate(`/anunturi-atribuire/${slug}/${navId}`);
    } else if (auctionType === 'PriorInformation') {
      navigate(`/anunturi-intentie/${slug}/${navId}`);
    }
  };

  return (
    <div
      className="bg-gradient-to-br from-indigo-50 to-white rounded-2xl shadow-lg hover:shadow-indigo-200 hover:scale-105 transition-all duration-300 p-6 cursor-pointer flex flex-col gap-4 border-t-4 border-indigo-400 group"
      onClick={handleNavigation}
    >
      {/* Header: ID (only for subscribers) + Save */}
      <div className="flex items-center justify-between gap-2">
        {hasActiveSubscription ? (
          <h2 className="text-indigo-600 font-bold text-lg">#{auctionId}</h2>
        ) : (
          <h2 className="text-gray-400 font-bold text-lg flex items-center gap-1">
            <HiOutlineLockClosed className="w-4 h-4" />
            <span className="text-xs text-gray-400">Cod disponibil cu abonament</span>
          </h2>
        )}
        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
          <SaveProcedureButton noticeNo={auctionId} variant="compact" />
        </div>
      </div>

      {/* Title - always visible */}
      <h3 className="text-2xl font-extrabold text-gray-800 group-hover:text-indigo-600 transition mb-1">
        {auctionTitle}
      </h3>

      {/* Authority - hidden for free users */}
      {hasActiveSubscription ? (
        <div className="flex items-center gap-2 text-gray-500 text-sm mb-2">
          <HiOutlineOfficeBuilding className="text-green-500 text-lg" />
          <span>{authorityName}</span>
        </div>
      ) : (
        <div className="flex items-center gap-2 text-sm mb-2">
          <HiOutlineLockClosed className="text-gray-300 text-lg" />
          <span className="text-gray-400 italic text-xs">Disponibil cu abonament activ</span>
        </div>
      )}

      {/* Info: Valoare + Data */}
      <div className="flex flex-wrap gap-6 items-center justify-between mt-2">
        <div className="flex items-center gap-2 text-gray-700">
          <HiOutlineCurrencyDollar className="text-green-600 text-xl" />
          <span className="font-semibold">
            {Utility.ConvertEstimatedValue(estimatedValue)}{' '}
            {currency}
          </span>
        </div>
        <div className="flex items-center gap-2 text-gray-700">
          <HiOutlineCalendar className="text-indigo-500 text-xl" />
          <span className="italic">Publicata la: {formattedDate}</span>
        </div>
      </div>

      {/* Actions - hidden for free users */}
      {hasActiveSubscription ? (
        <div className="flex flex-wrap items-center gap-2" onClick={(e) => e.stopPropagation()}>
          <ProjectBadge auctionId={auctionId} variant="compact" />
          <GenerateOfferButton auctionId={auctionId} variant="button" />
          <CDERequestButton auctionId={auctionId} variant="button" />
        </div>
      ) : (
        <div className="flex items-center gap-2 mt-1" onClick={(e) => e.stopPropagation()}>
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-400 rounded-lg text-xs">
            <HiOutlineLockClosed className="w-3.5 h-3.5" />
            Functionalitati premium
          </span>
        </div>
      )}
    </div>
  );
};

export default AuctionItem;
